/**
 * 캔버스 전용 UI + ethminions 이미지 에셋.
 * 패턴·내역은 상단 탭(▼/▲) 드롭다운 — 메인 게임 위 오버레이(레이아웃 너비 불변).
 */

import * as PIXI from 'pixi.js';
import {
  buildPatternBoardModel,
  type PatternBoardModel,
  computeHistoryStats,
  formatScoreboardBlockLine,
  formatScoreboardCountdown,
  historyBadgeLabel,
  type HistoryRowNorm,
  type PatternColorKey,
  PATTERN_CELL_PX,
  parseHistoryRows,
  sortHistoryChronological,
  sortHistoryNewestFirst,
  patternTabVisibility,
  type PatternTabId,
  formatVerifyResultEthminions,
} from '../ui/minionsHud';
import type { StatusHudModel } from '../ui/minionsHud';
import { loadMinionsTextureSet, type MinionsTextureSet } from './minionsAssets';
import { ETH_FRAME_H, ETH_FRAME_W, MinionsEthDecor } from './MinionsEthDecor';
import { MINIONS_UI } from '../config/minionsUiFlags';

const COL_ACCENT = 0xfacc15;
const COL_TEXT = 0xe2e8f0;
/** 상단 탭 — 열림(액센트) */
const COL_TAB_ACTIVE_STROKE = 0xeab308;
const COL_TAB_ACTIVE_TEXT = 0xfef08a;
const COL_TAB_INACTIVE_STROKE = 0x475569;
const COL_TAB_PILL_FILL = 0x131b2c;
const COL_TAB_PILL_FILL_ON = 0x1a2744;
/** ethminions .ml-analysis-panel / .ml-tab-body */
const ML_ANALYSIS_FRAME = 0xececec;
const ML_ANALYSIS_BORDER = 0xcfd3d7;
const ML_TAB_STRIP = 0xdfdfdf;
const ML_TAB_ACTIVE = 0xdc2626;
const ML_TAB_INACTIVE = 0x4b5563;
const ML_BODY_HINT = 0x6b7280;
const ML_TITLE_PILL_TEXT = 0x0b1220;
/** ethminions .ml-result-panel */
const ML_RESULT_BORDER = 0xf97316;
const ML_RESULT_TOP = 0xfef3c7;
const ML_RESULT_MID = 0xfbbf24;
/** ethminions .ml-result-list / .ml-verify-panel */
const ML_LIST_BG = 0x0f172a;
const ML_LIST_TEXT = 0xe5e7eb;
const ML_TAB_BOTTOM_ACCENT = 0xef4444;
const ML_RESULT_BOTTOM = 0xf59e0b;
/** 좌측 패턴 패널 탭 줄 높이(ethminions .ml-tabs) */
const LEFT_TAB_STRIP_H = 36;
const LEFT_TAB_CELL_W = 100;
/** 통계 pill 높이(2행까지 예약 → patternGridTop과 동기) */
const PATTERN_STAT_PILL_H = 24;
const PATTERN_STATS_VPAD = 6;
const PATTERN_STATS_TO_GRID = 10;
const PATTERN_STATS_BAND =
  PATTERN_STATS_VPAD + (2 * PATTERN_STAT_PILL_H + 2) + PATTERN_STATS_TO_GRID;
const ML_STAT_PILL_BG = 0xf8fafc;
const ML_STAT_PILL_BORDER = 0xd1d5db;
const ML_DOT_BLUE = 0x3b82f6;
const ML_DOT_RED = 0xef4444;
const ML_HEAD_BLUE = 0x0ea5e9;
const ML_HEAD_RED = 0xef4444;
const ML_CARD_BG = 0x0f172a;
const ML_CARD_BORDER = 0x374151;
const ML_CARD_META = 0xc4cbd5;
const ML_BLOCK_LINK = 0x93c5fd;

function txt(
  content: string,
  fontSize: number,
  fill: number,
  wordWrapWidth?: number
): PIXI.Text {
  return new PIXI.Text(
    content,
    new PIXI.TextStyle({
      fontFamily: 'Pretendard, system-ui, -apple-system, sans-serif',
      fontSize,
      fill,
      lineHeight: Math.round(fontSize * 1.35),
      wordWrap: wordWrapWidth != null,
      wordWrapWidth: wordWrapWidth ?? 400,
    })
  );
}

/** removeChildren 만 하면 Text 텍스처·GPU 리소스가 쌓여 시간이 지날수록 렉 발생 */
function destroyContainerChildren(c: PIXI.Container) {
  const removed = c.removeChildren(0, c.children.length);
  for (const d of removed) {
    d.destroy({ children: true, texture: true });
  }
}

export class MinionsCanvasUI extends PIXI.Container {
  private minibackSpr = new PIXI.Sprite(PIXI.Texture.WHITE);
  private topBar = new PIXI.Container();
  private topBarBg = new PIXI.Graphics();
  private patternTabBtn = new PIXI.Container();
  private historyTabBtn = new PIXI.Container();
  private patternTabBg = new PIXI.Graphics();
  private historyTabBg = new PIXI.Graphics();
  private patternTabText = txt('패턴 ▼', 12, COL_TEXT);
  private historyTabText = txt('내역 ▼', 12, COL_TEXT);

  private leftPanel = new PIXI.Container();
  private centerPanel = new PIXI.Container();
  private rightPanel = new PIXI.Container();
  private ethDecor = new MinionsEthDecor();

  private leftChrome = new PIXI.Graphics();
  private leftTitlePill = new PIXI.Graphics();
  private rightChrome = new PIXI.Graphics();
  private rightHistoryBackdrop = new PIXI.Graphics();
  private rightVerifyBackdrop = new PIXI.Graphics();

  private leftTitle: PIXI.Text;
  private tabRow = new PIXI.Container();
  private statsPills = new PIXI.Container();
  private patternArea = new PIXI.Container();
  /** 격자·도트만 스케일·가로 스크롤 (ethminions .ml-pattern-grid overflow-x) */
  private patternContent = new PIXI.Container();
  private patternMaskG = new PIXI.Graphics();
  private patternGridBg = new PIXI.Graphics();
  private patternDots = new PIXI.Container();
  private patternEmpty: PIXI.Text;
  private patternFooter: PIXI.Text;
  private tabButtons: {
    id: PatternTabId;
    container: PIXI.Container;
    bg: PIXI.Graphics;
    label: PIXI.Text;
  }[] = [];

  private rightTitle: PIXI.Text;
  private historyMaskG = new PIXI.Graphics();
  private historyInner = new PIXI.Container();
  private historyScrollRoot = new PIXI.Container();
  /** DOM 검증 패널 위치 맞춤용(투명) */
  private verifyOverlayProbe = new PIXI.Graphics();
  private centerTitle: PIXI.Text;
  /** ethDecor만 잘라 넘침 숨김(cover 스케일용) */
  private centerGameMask = new PIXI.Graphics();

  private patternTab: PatternTabId = 'odd_even';
  private historyScrollY = 0;
  private historyMaxScroll = 0;
  private histHit = { x: 0, y: 0, w: 0, h: 0 };
  /** 상단 드롭 탭 크기(layout에서 갱신) */
  private topTabW = 96;
  private topTabH = 30;
  private histDragging = false;
  private histLastPointerY = 0;

  private patternScrollX = 0;
  private patternScrollMax = 0;
  private patternHit = { x: 0, y: 0, w: 0, h: 0 };
  private patternHistoryLenSeen = -1;
  private patternTabForScroll: PatternTabId | null = null;
  private lastPatternBoardModel: PatternBoardModel | null = null;
  private lastPatternScale = 1;

  private panelH = 600;
  private rightPanelW = 260;
  private histTop = 36;
  private histH = 200;
  private patternGridTop = LEFT_TAB_STRIP_H + PATTERN_STATS_BAND;
  private patternGridH = 200;
  private leftPanelW = 260;

  private textureSet: MinionsTextureSet = {};

  /** 드롭다운 펼침 (env 로 초기값) */
  private patternDropOpen = false;
  private historyDropOpen = false;

  /** 1초 타이커: 전광판 문자열이 동일하면 Pixi Text 갱신 생략 */
  private lastScoreboardHudKey = '';

  private onPatternTabChange: () => void;
  private readonly canvasEl: HTMLCanvasElement;
  private verifyDomHost: HTMLDivElement | null = null;
  private verifyDomInput: HTMLInputElement | null = null;
  private verifyDomResult: HTMLDivElement | null = null;

  constructor(onPatternTabChange: () => void, canvasEl: HTMLCanvasElement) {
    super();
    this.onPatternTabChange = onPatternTabChange;
    this.canvasEl = canvasEl;
    this.sortableChildren = true;

    if (!MINIONS_UI.disablePattern) {
      this.patternDropOpen = MINIONS_UI.drawerStartOpen;
    }
    if (!MINIONS_UI.disableHistory) {
      this.historyDropOpen = MINIONS_UI.drawerStartOpen;
    }

    this.minibackSpr.visible = false;
    this.minibackSpr.alpha = 0;
    this.minibackSpr.zIndex = -20;
    this.addChild(this.minibackSpr);

    this.leftPanel.addChild(this.leftChrome);

    this.leftTitle = txt('', 1, ML_TITLE_PILL_TEXT, 10);
    this.leftTitle.visible = false;

    this.rightPanel.addChild(this.rightChrome);

    const tabs: PatternTabId[] = ['odd_even', 'left_right', 'ladder'];
    const tabLabels = ['홀짝패턴', '좌우패턴', '사다리수패턴'];
    let tx = 0;
    for (let i = 0; i < 3; i++) {
      const id = tabs[i];
      const container = new PIXI.Container();
      const g = new PIXI.Graphics();
      const label = txt(tabLabels[i], 12, ML_TAB_INACTIVE, LEFT_TAB_CELL_W - 4);
      label.style.fontWeight = '700';
      label.anchor.set(0.5);
      label.position.set(LEFT_TAB_CELL_W / 2, LEFT_TAB_STRIP_H / 2);
      container.addChild(g);
      container.addChild(label);
      container.position.set(tx, 0);
      container.interactive = true;
      container.cursor = 'pointer';
      container.hitArea = new PIXI.Rectangle(0, 0, LEFT_TAB_CELL_W, LEFT_TAB_STRIP_H);
      container.on('pointertap', (e: PIXI.InteractionEvent) => {
        e.stopPropagation();
        this.setPatternTab(id);
      });
      this.tabButtons.push({ id, container, bg: g, label });
      tx += LEFT_TAB_CELL_W;
    }
    this.tabRow.y = 0;
    this.tabButtons.forEach((t) => this.tabRow.addChild(t.container));
    this.leftPanel.addChild(this.tabRow);
    this.leftPanel.addChild(this.statsPills);

    this.patternMaskG.renderable = false;
    this.patternArea.addChild(this.patternMaskG);
    this.patternContent.addChild(this.patternGridBg);
    this.patternContent.addChild(this.patternDots);
    this.patternEmpty = txt('', 9, ML_BODY_HINT, 200);
    this.patternEmpty.visible = false;
    this.patternContent.addChild(this.patternEmpty);
    this.patternArea.addChild(this.patternContent);
    this.patternArea.mask = this.patternMaskG;
    this.leftPanel.addChild(this.patternArea);

    this.patternFooter = txt(
      '최근 흐름을 좌측부터 우측으로 확인합니다.',
      9,
      ML_BODY_HINT,
      260
    );
    this.leftPanel.addChild(this.patternFooter);
    this.leftPanel.addChild(this.leftTitlePill);
    this.leftPanel.addChild(this.leftTitle);
    this.leftTitlePill.visible = false;

    this.centerTitle = txt('미니언즈 사다리', 14, COL_ACCENT, 280);
    this.centerTitle.anchor.set(0.5, 0);
    this.centerPanel.addChild(this.centerTitle);
    this.centerPanel.addChild(this.centerGameMask);
    this.centerPanel.addChild(this.ethDecor);
    this.ethDecor.mask = this.centerGameMask;
    this.centerGameMask.visible = false;

    this.rightTitle = txt('내역', 14, 0x7c2d12, 260);
    this.historyInner.addChild(this.historyScrollRoot);
    this.historyInner.mask = this.historyMaskG;
    this.historyInner.interactive = true;
    this.historyInner.on('pointerdown', this.onHistPointerDown);
    this.historyInner.on('pointermove', this.onHistPointerMove);
    this.historyInner.on('pointerup', this.onHistPointerUp);
    this.historyInner.on('pointerupoutside', this.onHistPointerUp);
    this.historyInner.on('pointercancel', this.onHistPointerUp);

    this.rightPanel.addChild(this.rightTitle);
    this.rightPanel.addChild(this.rightHistoryBackdrop);
    this.rightPanel.addChild(this.historyMaskG);
    this.rightPanel.addChild(this.historyInner);
    this.rightPanel.addChild(this.rightVerifyBackdrop);

    this.verifyOverlayProbe.interactive = false;
    this.verifyOverlayProbe.visible = false;
    this.rightPanel.addChild(this.verifyOverlayProbe);
    this.mountVerifyDom();

    this.setupTopBarTabs();

    this.addChild(this.centerPanel);
    this.addChild(this.leftPanel);
    this.addChild(this.rightPanel);
    this.topBar.zIndex = 100;
    this.centerPanel.zIndex = 0;
    /* 패턴·내역: 메인 게임 너비/위치 불변, 위에 겹침(오버레이) */
    this.leftPanel.zIndex = 70;
    this.rightPanel.zIndex = 70;
    this.addChild(this.topBar);

    this.redrawTabs();
    this.redrawDropTabs();
  }

  private setupTopBarTabs() {
    this.topBar.addChild(this.topBarBg);
    this.topBarBg.interactive = false;

    this.patternTabBtn.addChild(this.patternTabBg);
    this.patternTabText.anchor.set(0.5, 0.5);
    this.patternTabBtn.addChild(this.patternTabText);
    this.patternTabBtn.interactive = true;
    this.patternTabBtn.cursor = 'pointer';
    this.patternTabBtn.hitArea = new PIXI.Rectangle(0, 0, this.topTabW, this.topTabH);
    this.patternTabBtn.on('pointertap', (e: PIXI.InteractionEvent) =>
      this.togglePatternDropFromTopBar(e)
    );

    this.historyTabBtn.addChild(this.historyTabBg);
    this.historyTabText.anchor.set(0.5, 0.5);
    this.historyTabBtn.addChild(this.historyTabText);
    this.historyTabBtn.interactive = true;
    this.historyTabBtn.cursor = 'pointer';
    this.historyTabBtn.hitArea = new PIXI.Rectangle(0, 0, this.topTabW, this.topTabH);
    this.historyTabBtn.on('pointertap', (e: PIXI.InteractionEvent) =>
      this.toggleHistoryDropFromTopBar(e)
    );

    this.topBar.addChild(this.patternTabBtn);
    this.topBar.addChild(this.historyTabBtn);
  }

  /**
   * 패널이 펼쳐진 뒤 클릭이 좌측 패턴 영역(탭 줄)으로 내려가도 상단「패턴」버튼과 동시에 잡히면
   * 드로어만 닫히는 현상이 있어, 글로벌 Y가 패널 본문 이하면 탑바 토글은 무시.
   */
  private togglePatternDropFromTopBar(e: PIXI.InteractionEvent) {
    if (MINIONS_UI.disablePattern) return;
    const gy = e.data.global.y;
    if (
      this.patternDropOpen &&
      this.leftPanel.visible &&
      gy >= this.leftPanel.y - 0.5
    ) {
      return;
    }
    this.patternDropOpen = !this.patternDropOpen;
    this.redrawDropTabs();
    this.onPatternTabChange();
  }

  private toggleHistoryDropFromTopBar(e: PIXI.InteractionEvent) {
    if (MINIONS_UI.disableHistory) return;
    const gy = e.data.global.y;
    if (
      this.historyDropOpen &&
      this.rightPanel.visible &&
      gy >= this.rightPanel.y - 0.5
    ) {
      return;
    }
    this.historyDropOpen = !this.historyDropOpen;
    this.redrawDropTabs();
    this.onPatternTabChange();
  }

  private redrawDropTabs() {
    const pw = this.patternDropOpen && !MINIONS_UI.disablePattern;
    const hw = this.historyDropOpen && !MINIONS_UI.disableHistory;
    this.patternTabText.text = `패턴 ${pw ? '▲' : '▼'}`;
    this.historyTabText.text = `내역 ${hw ? '▲' : '▼'}`;

    const drawTabBg = (g: PIXI.Graphics, active: boolean, w: number, h: number) => {
      g.clear();
      const r = Math.min(14, h / 2 - 1);
      g.beginFill(0x000000, 0.22);
      g.drawRoundedRect(1, 2, w, h, r);
      g.endFill();
      g.lineStyle(active ? 2 : 1, active ? COL_TAB_ACTIVE_STROKE : COL_TAB_INACTIVE_STROKE, 1);
      g.beginFill(active ? COL_TAB_PILL_FILL_ON : COL_TAB_PILL_FILL, 1);
      g.drawRoundedRect(0, 0, w, h, r);
      g.endFill();
      g.lineStyle(0);
      g.beginFill(0xffffff, active ? 0.07 : 0.035);
      g.drawRoundedRect(2, 2, w - 4, Math.max(4, h * 0.38), Math.max(3, r - 2));
      g.endFill();
    };
    drawTabBg(this.patternTabBg, pw, this.topTabW, this.topTabH);
    drawTabBg(this.historyTabBg, hw, this.topTabW, this.topTabH);

    this.patternTabText.style.fill = pw ? COL_TAB_ACTIVE_TEXT : COL_TEXT;
    this.patternTabText.style.fontWeight = pw ? '800' : '600';
    this.historyTabText.style.fill = hw ? COL_TAB_ACTIVE_TEXT : COL_TEXT;
    this.historyTabText.style.fontWeight = hw ? '800' : '600';
  }

  getPatternTab(): PatternTabId {
    return this.patternTab;
  }

  async loadEthAssets(): Promise<void> {
    try {
      this.textureSet = await loadMinionsTextureSet();
      const mb = this.textureSet.miniback;
      if (mb?.valid) {
        this.minibackSpr.texture = mb;
        this.minibackSpr.visible = true;
        this.minibackSpr.alpha = 1;
      } else {
        this.minibackSpr.visible = false;
      }
      this.ethDecor.applyTextures(this.textureSet);
    } catch {
      /* keep 폴백 UI */
    }
  }

  mountBoard(board: PIXI.Container) {
    this.ethDecor.mountBoard(board);
  }

  private setPatternTab(id: PatternTabId) {
    this.patternTab = id;
    this.redrawTabs();
    this.onPatternTabChange();
  }

  private redrawTabs() {
    const last = this.tabButtons.length - 1;
    for (let i = 0; i < this.tabButtons.length; i++) {
      const t = this.tabButtons[i];
      const on = t.id === this.patternTab;
      t.bg.clear();
      if (on) {
        t.bg.beginFill(0xffffff, 1);
        t.bg.drawRect(0, 0, LEFT_TAB_CELL_W, LEFT_TAB_STRIP_H);
        t.bg.endFill();
      }
      if (i < last) {
        t.bg.lineStyle(1, ML_ANALYSIS_BORDER, 1);
        t.bg.moveTo(LEFT_TAB_CELL_W - 0.5, 0);
        t.bg.lineTo(LEFT_TAB_CELL_W - 0.5, LEFT_TAB_STRIP_H);
      }
      if (on) {
        t.bg.lineStyle(2, ML_TAB_BOTTOM_ACCENT, 1);
        t.bg.moveTo(0, LEFT_TAB_STRIP_H - 1);
        t.bg.lineTo(LEFT_TAB_CELL_W, LEFT_TAB_STRIP_H - 1);
      }
      t.label.style.fill = on ? ML_TAB_ACTIVE : ML_TAB_INACTIVE;
    }
  }

  private onHistPointerDown = (e: PIXI.InteractionEvent) => {
    if (!this.rightPanel.visible || !this.historyDropOpen) return;
    if (this.historyMaxScroll <= 0) return;
    this.histDragging = true;
    this.histLastPointerY = e.data.global.y;
  };

  private onHistPointerMove = (e: PIXI.InteractionEvent) => {
    if (!this.histDragging) return;
    const y = e.data.global.y;
    const dy = y - this.histLastPointerY;
    this.histLastPointerY = y;
    this.historyScrollY -= dy;
    this.historyScrollY = Math.max(0, Math.min(this.historyMaxScroll, this.historyScrollY));
    this.historyScrollRoot.y = -this.historyScrollY;
  };

  private onHistPointerUp = () => {
    this.histDragging = false;
  };

  private layoutMiniback(width: number, height: number) {
    if (!this.minibackSpr.visible || !this.minibackSpr.texture?.valid) return;
    const tw = this.minibackSpr.texture.width;
    const th = this.minibackSpr.texture.height;
    if (tw < 1 || th < 1) return;
    const sx = width / tw;
    const sy = height / th;
    /** 전체 화면을 덮도록 항상 cover(CSS background-size: cover 와 동일) */
    const s = Math.max(sx, sy);
    this.minibackSpr.scale.set(s);
    this.minibackSpr.anchor.set(0.5);
    this.minibackSpr.position.set(width / 2, height / 2);
  }

  layout(width: number, height: number) {
    /* 428~430px 급 폰은 420 미만이 아니라 기존엔 compact=false → 게임·배경이 cover 로 잘림 */
    const compact = width < 480 || height < 520;
    // 좁은 폭에서는 내역/패턴 드로어를 화면 전폭(=부모 폭)으로 사용
    const drawerFullWidth = width <= 880;
    const landscape = width > height;
    const shortH = height < 480;
    const pad = compact ? 6 : 10;
    const tabGap = compact ? 8 : 12;
    const barUsableW = Math.max(0, width - pad * 2);
    this.topTabH = compact ? 32 : 36;
    /* 좁은 화면: 좌·우 탭이 남는 폭을 반반(간격 tabGap) — 고정 폭이면 제목 없이도 우측만 붙어 레이아웃이 어색함 */
    this.topTabW = Math.max(72, Math.floor((barUsableW - tabGap) / 2));
    const topBarInner = this.topTabH + pad + 6;
    /* 반응형: 가로·저세로에선 사이드 최소 폭·열기 폭 비율을 낮춰 가운데 게임 영역 확보 */
    const sideMinFrac = landscape && shortH ? 0.34 : landscape ? 0.38 : width < 480 ? 0.44 : 0.46;
    const sideOpenFrac = landscape && shortH ? 0.26 : landscape ? 0.28 : 0.3;
    const minSideCap = landscape && shortH ? 240 : landscape ? 268 : 300;
    const minSide = Math.max(136, Math.min(minSideCap, Math.round(width * sideMinFrac)));
    const canPattern = !MINIONS_UI.disablePattern;
    const canHistory = !MINIONS_UI.disableHistory;

    const contentTop = pad + topBarInner + 6;
    this.panelH = Math.max(100, height - contentTop - pad);

    this.patternTabText.style.fontSize = compact ? 11 : 13;
    this.historyTabText.style.fontSize = compact ? 11 : 13;
    this.patternTabText.position.set(this.topTabW / 2, this.topTabH / 2);
    this.historyTabText.position.set(this.topTabW / 2, this.topTabH / 2);

    this.topBar.position.set(0, 0);
    this.topBarBg.clear();
    this.topBarBg.beginFill(0x070b12, 0.98);
    this.topBarBg.drawRect(0, 0, width, topBarInner + pad);
    this.topBarBg.endFill();
    this.topBarBg.lineStyle(1, 0xfacc15, 0.14);
    this.topBarBg.moveTo(0, topBarInner + pad - 0.5);
    this.topBarBg.lineTo(width, topBarInner + pad - 0.5);

    this.patternTabBtn.hitArea = new PIXI.Rectangle(0, 0, this.topTabW, this.topTabH);
    this.historyTabBtn.hitArea = new PIXI.Rectangle(0, 0, this.topTabW, this.topTabH);

    // VITE_MINIONS_DISABLE_* 는 패널·토글 동작만 막음. 상단「패턴▼」「내역▼」은 항상 표시해 접근 경로 유지.
    this.patternTabBtn.visible = true;
    this.historyTabBtn.visible = true;
    this.patternTabBtn.alpha = canPattern ? 1 : 0.4;
    this.historyTabBtn.alpha = canHistory ? 1 : 0.4;
    this.patternTabBtn.interactive = canPattern;
    this.historyTabBtn.interactive = canHistory;
    this.patternTabBtn.cursor = canPattern ? 'pointer' : 'default';
    this.historyTabBtn.cursor = canHistory ? 'pointer' : 'default';
    this.patternTabBtn.position.set(pad, pad);
    this.historyTabBtn.position.set(pad + this.topTabW + tabGap, pad);

    let leftW = 0;
    if (canPattern && this.patternDropOpen) {
      leftW = drawerFullWidth
        ? Math.max(120, width - pad * 2)
        : Math.max(minSide, Math.min(width * sideOpenFrac, minSideCap));
      this.strokePanel(this.leftChrome, leftW, this.panelH);
      this.leftPanelW = leftW;
      this.leftPanel.position.set(pad, contentTop);
      this.leftPanel.visible = true;
      const wrap = Math.max(56, leftW - 20);
      this.patternFooter.style.wordWrapWidth = wrap;
      this.patternEmpty.style.wordWrapWidth = Math.max(80, leftW - 28);
      const tabTarget = LEFT_TAB_CELL_W * 3;
      const tabScale = Math.min(1, Math.max(0.55, (leftW - 8) / tabTarget));
      this.tabRow.scale.set(tabScale);
      this.tabRow.position.x = Math.round(Math.max(0, (leftW - tabTarget * tabScale) / 2));
      this.sharpenPatternTabLabels(tabScale);
      this.patternGridTop = LEFT_TAB_STRIP_H + PATTERN_STATS_BAND;
      /* PHP .ml-pattern-grid 는 본문을 크게 쓰므로 남는 세로를 격자에 할당(상한만 완만히) */
      this.patternGridH = Math.max(
        compact ? 100 : 140,
        Math.min(
          Math.floor(this.panelH * 0.62),
          Math.max(72, this.panelH - this.patternGridTop - (compact ? 24 : 36))
        )
      );
      this.statsPills.position.set(6, LEFT_TAB_STRIP_H + PATTERN_STATS_VPAD);
      this.patternArea.position.set(8, this.patternGridTop);
      this.patternMaskG.clear();
      this.patternMaskG.beginFill(0xffffff);
      this.patternMaskG.drawRoundedRect(0, 0, Math.max(40, leftW - 16), this.patternGridH, 8);
      this.patternMaskG.endFill();
      this.patternFooter.position.set(8, Math.max(this.patternGridTop + this.patternGridH + 6, this.panelH - 22));
      const pw = Math.max(40, leftW - 16);
      this.patternHit = {
        x: this.leftPanel.x + 8,
        y: this.leftPanel.y + this.patternGridTop,
        w: pw,
        h: this.patternGridH,
      };
    } else {
      this.leftPanel.visible = false;
      this.tabRow.scale.set(1);
      this.tabRow.position.x = 0;
      this.sharpenPatternTabLabels(1);
      this.patternHit = { x: 0, y: 0, w: 0, h: 0 };
    }

    let rightW = 0;
    if (canHistory && this.historyDropOpen) {
      rightW = drawerFullWidth
        ? Math.max(120, width - pad * 2)
        : Math.max(minSide, Math.min(width * sideOpenFrac, minSideCap));
      this.rightPanelW = rightW;
      this.strokeHistoryPanel(this.rightChrome, rightW, this.panelH);
      this.rightPanel.position.set(drawerFullWidth ? pad : width - pad - rightW, contentTop);
      this.rightPanel.visible = true;

      this.histTop = 36;
      /* 검증 패널 세로: 뷰포트가 낮을수록 내역·검증 비율 가변 (고정 300px 예약은 가로 모드에서 과도) */
      const histReserve = Math.min(300, Math.max(168, Math.round(this.panelH * 0.52)));
      this.histH = Math.max(72, this.panelH - histReserve);

      this.rightTitle.position.set(12, 8);

      const innerHistW = Math.max(40, rightW - 24);
      this.historyMaskG.clear();
      this.historyMaskG.beginFill(0xffffff);
      this.historyMaskG.drawRoundedRect(12, this.histTop, innerHistW, this.histH, 8);
      this.historyMaskG.endFill();

      this.historyInner.position.set(12, this.histTop);
      this.historyInner.hitArea = new PIXI.Rectangle(0, 0, innerHistW, this.histH);
      this.historyInner.interactive = true;

      this.histHit = {
        x: this.rightPanel.x + 12,
        y: this.rightPanel.y + this.histTop,
        w: innerHistW,
        h: this.histH,
      };

      const verifyY = this.histTop + this.histH + 12;
      const verifyBlockTop = verifyY - 6;
      const verifyMinH = compact ? 132 : 168;
      const verifyBlockH = Math.max(
        verifyMinH,
        Math.max(96, this.panelH - verifyBlockTop - 8)
      );
      this.rightVerifyBackdrop.clear();
      this.rightVerifyBackdrop.lineStyle(1, 0x94a3b8, 0.55);
      this.rightVerifyBackdrop.beginFill(0x0f172a, 0.85);
      this.rightVerifyBackdrop.drawRoundedRect(8, verifyBlockTop, rightW - 16, verifyBlockH, 12);
      this.rightVerifyBackdrop.endFill();

      this.rightHistoryBackdrop.clear();
      this.rightHistoryBackdrop.lineStyle(1, 0x374151, 0.9);
      this.rightHistoryBackdrop.beginFill(ML_LIST_BG, 0.88);
      this.rightHistoryBackdrop.drawRoundedRect(12, this.histTop, innerHistW, this.histH, 8);
      this.rightHistoryBackdrop.endFill();

      this.verifyOverlayProbe.visible = true;
      this.verifyOverlayProbe.clear();
      this.verifyOverlayProbe.beginFill(0xffffff, 0.001);
      this.verifyOverlayProbe.drawRect(8, verifyBlockTop, rightW - 16, verifyBlockH);
      this.verifyOverlayProbe.endFill();
    } else {
      this.verifyOverlayProbe.visible = false;
      this.rightPanel.visible = false;
      this.historyInner.hitArea = null;
      this.historyInner.interactive = false;
      this.histDragging = false;
      this.histHit = { x: 0, y: 0, w: 0, h: 0 };
    }

    const centerW = Math.max(200, width - pad * 2);
    this.centerPanel.position.set(pad, contentTop);

    const titleH = 22;
    this.centerTitle.position.set(centerW / 2, 0);
    this.centerTitle.visible = false;

    const innerW = centerW;
    const innerH = this.panelH - titleH - 6;
    // 기본은 cover(꽉 채움)인데, 화면이 좁거나 세로가 짧으면 cover가 상/하를 잘라먹기 쉬움.
    // 그런 환경에서는 contain(잘림 없이 전체 표시)로 전환해 하단 UI가 짤리지 않게 한다.
    const sx = innerW / ETH_FRAME_W;
    const sy = innerH / ETH_FRAME_H;
    /** 세로 모드(폰 대부분): cover 는 프레임이 잘려 사다리·버튼이 안 보일 수 있음 → 항상 contain */
    const useContain =
      compact || shortH || innerH < ETH_FRAME_H * 0.78 || width < height;
    const s = useContain ? Math.min(sx, sy) : Math.max(sx, sy);
    const portrait = width < height;
    /** 세로: inner 가 길 때 세로 중앙 정렬이 상단 검은 공간처럼 보임 → 타이틀 아래로 바로 붙임 */
    const decorY = portrait
      ? titleH
      : titleH + Math.max(0, (innerH - ETH_FRAME_H * s) / 2);
    this.ethDecor.scale.set(s);
    this.ethDecor.position.set((centerW - ETH_FRAME_W * s) / 2, decorY);

    this.centerGameMask.clear();
    this.centerGameMask.beginFill(0xffffff);
    this.centerGameMask.drawRect(0, titleH, innerW, innerH);
    this.centerGameMask.endFill();

    this.layoutMiniback(width, height);

    const { w: bw, h: bh } = this.ethDecor.getBoardDesignSize();
    return { boardW: bw, boardH: bh };
  }

  /** ethminions .ml-analysis-panel + .ml-tabs + .ml-tab-body */
  private strokePanel(g: PIXI.Graphics, w: number, h: number) {
    g.clear();
    const r = 14;
    const stripH = LEFT_TAB_STRIP_H;
    g.beginFill(0x0f172a, 0.12);
    g.drawRoundedRect(2, 3, w, h, r);
    g.endFill();
    g.lineStyle(1, ML_ANALYSIS_BORDER, 1);
    g.beginFill(ML_ANALYSIS_FRAME, 1);
    g.drawRoundedRect(0, 0, w, h, r);
    g.endFill();
    g.lineStyle(0);
    g.beginFill(ML_TAB_STRIP, 1);
    g.drawRect(1, 1, w - 2, stripH - 1);
    g.endFill();
    g.lineStyle(1, ML_ANALYSIS_BORDER, 1);
    g.moveTo(1, stripH);
    g.lineTo(w - 1, stripH);
    g.lineStyle(0);
    g.beginFill(0xffffff, 1);
    g.drawRect(1, stripH, w - 2, Math.max(0, h - stripH - 1));
    g.endFill();
  }

  /** ethminions .ml-result-panel */
  private strokeHistoryPanel(g: PIXI.Graphics, w: number, h: number) {
    g.clear();
    const r = 20;
    g.beginFill(0x000000, 0.1);
    g.drawRoundedRect(3, 4, w, h, r);
    g.endFill();
    g.lineStyle(2, ML_RESULT_BORDER, 1);
    g.beginFill(ML_RESULT_TOP, 1);
    g.drawRoundedRect(0, 0, w, h, r);
    g.endFill();
    g.lineStyle(0);
    const y1 = Math.floor(h * 0.38);
    g.beginFill(ML_RESULT_MID, 0.45);
    g.drawRect(3, y1, w - 6, h - y1 - 3);
    g.endFill();
    g.beginFill(ML_RESULT_BOTTOM, 0.22);
    g.drawRect(3, Math.floor(h * 0.62), w - 6, h - Math.floor(h * 0.62) - 3);
    g.endFill();
  }

  refresh(
    width: number,
    height: number,
    status: StatusHudModel,
    history: Record<string, unknown>[]
  ) {
    const { boardW, boardH } = this.layout(width, height);

    this.redrawDropTabs();

    const normRows = parseHistoryRows(history);
    if (!MINIONS_UI.disablePattern && this.patternDropOpen) {
      const chronological = sortHistoryChronological(normRows);
      const stats = computeHistoryStats(history);
      this.rebuildStatsPills(stats, this.patternTab, this.leftPanelW);
      this.rebuildPatternBoard(chronological, this.patternTab);
    }

    if (!MINIONS_UI.disableHistory && this.historyDropOpen) {
      const cardW = Math.max(40, this.rightPanelW - 28);
      const listH = this.rebuildHistoryCards(sortHistoryNewestFirst(normRows), cardW);
      this.historyMaxScroll = Math.max(0, listH - this.histH);
      this.historyScrollY = Math.max(0, Math.min(this.historyMaxScroll, this.historyScrollY));
      this.historyScrollRoot.y = -this.historyScrollY;
    }

    this.applyScoreboardHud(status);

    this.syncVerifyDomOverlay(width, height);

    return { boardW, boardH };
  }

  private applyScoreboardHud(status: StatusHudModel) {
    const sbVis = status.scoreboardVisible !== false;
    const blockLine = formatScoreboardBlockLine(status.nextBlock);
    const countdownLine = formatScoreboardCountdown(status);
    const key = `${sbVis}\0${blockLine}\0${countdownLine}`;
    if (key === this.lastScoreboardHudKey) return;
    this.lastScoreboardHudKey = key;
    this.ethDecor.updateScoreboard({
      visible: sbVis,
      blockLine,
      countdownLine,
    });
  }

  /**
   * layout·패턴·내역 재구성 없이 전광판·검증 오버레이만 갱신(1초 타이머용).
   * 내역 드로어가 닫히면 DOM 검증 오버레이 동기화(getBounds)는 생략.
   */
  tickHudOnly(width: number, height: number, status: StatusHudModel) {
    this.applyScoreboardHud(status);
    if (
      !MINIONS_UI.disableHistory &&
      this.historyDropOpen &&
      this.rightPanel.visible
    ) {
      this.syncVerifyDomOverlay(width, height);
    }
  }

  handleWheel(canvasX: number, canvasY: number, deltaY: number) {
    const ph = this.patternHit;
    if (
      ph.w > 0 &&
      ph.h > 0 &&
      this.patternDropOpen &&
      !MINIONS_UI.disablePattern &&
      this.patternScrollMax > 0 &&
      canvasX >= ph.x &&
      canvasX <= ph.x + ph.w &&
      canvasY >= ph.y &&
      canvasY <= ph.y + ph.h
    ) {
      this.patternScrollX += deltaY * 0.55;
      this.patternScrollX = Math.max(0, Math.min(this.patternScrollMax, this.patternScrollX));
      this.applyPatternContentScroll();
      return;
    }

    const { x, y, w, h } = this.histHit;
    if (canvasX < x || canvasX > x + w || canvasY < y || canvasY > y + h) return;
    this.historyScrollY += deltaY * 0.45;
    this.historyScrollY = Math.max(0, Math.min(this.historyMaxScroll, this.historyScrollY));
    this.historyScrollRoot.y = -this.historyScrollY;
  }

  /** 가로 스크롤만 갱신(휠 후 layout 없이) */
  private applyPatternContentScroll() {
    const availW = Math.max(40, this.leftPanelW - 16);
    const availH = Math.max(60, this.patternGridH);
    const model = this.lastPatternBoardModel;
    if (!model) return;
    const sc = this.lastPatternScale;
    const contentW = model.boardW * sc;
    const contentH = model.boardH * sc;
    const x = contentW <= availW ? (availW - contentW) / 2 : -this.patternScrollX;
    const y = Math.max(0, (availH - contentH) / 2);
    this.patternContent.position.set(x, y);
  }

  private patternColor(c: PatternColorKey): number {
    if (c === 'blue') return ML_DOT_BLUE;
    if (c === 'red') return ML_DOT_RED;
    return 0x6b7280;
  }

  private patternHeadColor(c: PatternColorKey): number {
    if (c === 'blue') return ML_HEAD_BLUE;
    if (c === 'red') return ML_HEAD_RED;
    return 0x6b7280;
  }

  /** tabRow 축소(scale < 1) 시 라벨 텍스처가 줄어들며 번짐 → 내부 resolution 보정 */
  private sharpenPatternTabLabels(tabRowScale: number): void {
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const shrink = tabRowScale > 0 && tabRowScale < 0.999 ? 1 / tabRowScale : 1;
    const boost =
      shrink > 1.02
        ? Math.min(4, Math.max(2, Math.ceil(dpr * shrink)))
        : Math.min(3, Math.max(2, Math.ceil(dpr)));
    for (const t of this.tabButtons) {
      const pt = t.label as PIXI.Text & { _autoResolution: boolean };
      pt._autoResolution = false;
      t.label.resolution = boost;
      t.label.roundPixels = false;
    }
  }

  /**
   * patternContent에 scale이 걸리면 Text 텍스처가 확대 샘플링되어 번짐 → 내부 resolution 상향.
   */
  private sharpenTextForParentScale(t: PIXI.Text, parentScale: number): void {
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const boost = Math.min(
      5,
      Math.max(2, Math.ceil(dpr * Math.max(1, parentScale * 1.2)))
    );
    const pt = t as PIXI.Text & { _autoResolution: boolean };
    pt._autoResolution = false;
    t.resolution = boost;
    t.roundPixels = false;
  }

  private rebuildStatsPills(stats: ReturnType<typeof computeHistoryStats>, tab: PatternTabId, panelW: number) {
    destroyContainerChildren(this.statsPills);
    const vis = patternTabVisibility(tab);
    const pairs: { label: string; value: number; on: boolean }[] = [];
    if (vis.oddEven) {
      pairs.push({ label: '홀', value: stats.odd, on: true }, { label: '짝', value: stats.even, on: true });
    }
    if (vis.leftRight) {
      pairs.push({ label: '좌', value: stats.left, on: true }, { label: '우', value: stats.right, on: true });
    }
    if (vis.ladder) {
      pairs.push({ label: '3줄', value: stats.line3, on: true }, { label: '4줄', value: stats.line4, on: true });
    }
    const gap = 6;
    const pillH = PATTERN_STAT_PILL_H;
    const pillW = Math.max(52, (Math.max(80, panelW - 12) - gap) / 2);
    let x = 0;
    let y = 0;
    for (let i = 0; i < pairs.length; i++) {
      const p = pairs[i];
      const wrap = new PIXI.Container();
      const g = new PIXI.Graphics();
      g.lineStyle(1, ML_STAT_PILL_BORDER, 1);
      g.beginFill(ML_STAT_PILL_BG, 1);
      g.drawRoundedRect(0, 0, pillW, pillH, 8);
      g.endFill();
      const t = new PIXI.Text(
        `${p.label} ${p.value}`,
        new PIXI.TextStyle({
          fontFamily: 'Pretendard, system-ui, -apple-system, sans-serif',
          fontSize: 14,
          fontWeight: '800',
          fill: 0x0f172a,
          lineHeight: Math.round(14 * 1.2),
          wordWrap: true,
          wordWrapWidth: pillW - 6,
          align: 'center',
        })
      );
      t.anchor.set(0.5, 0.5);
      t.position.set(pillW / 2, pillH / 2);
      this.sharpenTextForParentScale(t, 1);
      wrap.addChild(g);
      wrap.addChild(t);
      wrap.position.set(x, y);
      this.statsPills.addChild(wrap);
      x += pillW + gap;
      if (i % 2 === 1) {
        x = 0;
        y += pillH + 2;
      }
    }
  }

  private rebuildPatternBoard(chronological: HistoryRowNorm[], tab: PatternTabId) {
    const model = buildPatternBoardModel(chronological, tab);
    this.lastPatternBoardModel = model;
    this.patternFooter.text = model.emptyMessage
      ? '데이터가 없습니다.'
      : model.footer;

    const availW = Math.max(40, this.leftPanelW - 16);
    const availH = Math.max(60, this.patternGridH);
    /*
     * PHP: 셀 크기 고정 + 가로 스크롤. 데이터가 많아져 너비만 커질 때 세로 맞춤 스케일은 유지하고
     * 가로는 잘라 스크롤로 본다 → 전체를 한 화면에 넣느라 sc가 미세해지는 현상 방지.
     */
    this.patternArea.scale.set(1);
    const scRaw = Math.min(4, Math.max(0.35, availH / model.boardH));
    const sc = Math.round(scRaw * 8) / 8;
    this.lastPatternScale = sc;
    this.patternContent.scale.set(sc);

    const contentW = model.boardW * sc;
    const contentH = model.boardH * sc;
    this.patternScrollMax = Math.max(0, contentW - availW);

    const len = chronological.length;
    const tabChanged = this.patternTabForScroll !== tab;
    if (this.patternTabForScroll == null) {
      this.patternScrollX = this.patternScrollMax;
    } else if (tabChanged || len > this.patternHistoryLenSeen) {
      this.patternScrollX = this.patternScrollMax;
    } else {
      this.patternScrollX = Math.max(0, Math.min(this.patternScrollX, this.patternScrollMax));
    }
    this.patternHistoryLenSeen = len;
    this.patternTabForScroll = tab;

    const x = contentW <= availW ? (availW - contentW) / 2 : -this.patternScrollX;
    const y = Math.max(0, (availH - contentH) / 2);
    this.patternContent.position.set(x, y);

    this.patternGridBg.clear();
    this.patternGridBg.lineStyle(1.5, 0x9ca3af, 1);
    this.patternGridBg.beginFill(0xffffff, 1);
    this.patternGridBg.drawRoundedRect(0, 0, model.boardW, model.boardH, 4);
    this.patternGridBg.endFill();
    this.patternGridBg.lineStyle(1, 0xc4ccd6, 1);
    for (let gx = 0; gx <= model.boardW; gx += PATTERN_CELL_PX) {
      this.patternGridBg.moveTo(gx, 0);
      this.patternGridBg.lineTo(gx, model.boardH);
    }
    for (let gy = 0; gy <= model.boardH; gy += PATTERN_CELL_PX) {
      this.patternGridBg.moveTo(0, gy);
      this.patternGridBg.lineTo(model.boardW, gy);
    }

    destroyContainerChildren(this.patternDots);
    if (model.emptyMessage) {
      this.patternEmpty.visible = true;
      this.patternEmpty.text = model.emptyMessage;
      this.patternEmpty.anchor.set(0.5, 0.5);
      this.patternEmpty.position.set(model.boardW / 2, model.boardH / 2);
      this.sharpenTextForParentScale(this.patternEmpty, sc);
      this.patternScrollMax = 0;
      this.patternScrollX = 0;
      return;
    }
    this.patternEmpty.visible = false;

    for (const h of model.heads) {
      const ht = new PIXI.Text(
        h.label,
        new PIXI.TextStyle({
          fontFamily: 'Pretendard, system-ui, sans-serif',
          fontSize: 12,
          fontWeight: '900',
          fill: this.patternHeadColor(h.color),
        })
      );
      ht.anchor.set(0.5);
      ht.position.set(h.cx, h.cy);
      this.sharpenTextForParentScale(ht, sc);
      this.patternDots.addChild(ht);
    }

    const dotR = 10;
    for (const d of model.dots) {
      const wrap = new PIXI.Container();
      wrap.position.set(d.cx, d.cy);
      const circ = new PIXI.Graphics();
      circ.lineStyle(1.5, 0x000000, 0.18);
      circ.beginFill(this.patternColor(d.color), 1);
      circ.drawCircle(0, 0, dotR);
      circ.endFill();
      const fs = d.roundLabel.length >= 4 ? 9 : 11;
      const lab = new PIXI.Text(
        d.roundLabel,
        new PIXI.TextStyle({
          fontFamily: 'Pretendard, system-ui, sans-serif',
          fontSize: fs,
          fontWeight: '800',
          fill: 0xffffff,
          stroke: 0x1e293b,
          strokeThickness: 1.5,
        })
      );
      lab.anchor.set(0.5);
      this.sharpenTextForParentScale(lab, sc);
      wrap.addChild(circ);
      wrap.addChild(lab);
      this.patternDots.addChild(wrap);
    }
  }

  /** @returns 스크롤 콘텐츠 높이 */
  private rebuildHistoryCards(rows: HistoryRowNorm[], cardInnerW: number): number {
    destroyContainerChildren(this.historyScrollRoot);
    let y = 4;
    const maxN = 50;
    const slice = rows.slice(0, maxN);
    if (!slice.length) {
      const empty = txt('기록 없음 · API/프록시 확인', 11, 0x94a3b8, cardInnerW - 8);
      empty.position.set(4, 8);
      this.historyScrollRoot.addChild(empty);
      return empty.height + 24;
    }
    for (const row of slice) {
      const card = this.makeHistoryCard(row, cardInnerW);
      card.position.set(0, y);
      this.historyScrollRoot.addChild(card);
      y += 72;
    }
    return y + 8;
  }

  private makeHistoryCard(row: HistoryRowNorm, w: number): PIXI.Container {
    const c = new PIXI.Container();
    const h = 66;
    const bg = new PIXI.Graphics();
    bg.lineStyle(1, ML_CARD_BORDER, 0.95);
    bg.beginFill(ML_CARD_BG, 0.78);
    bg.drawRoundedRect(0, 0, w, h, 10);
    bg.endFill();
    c.addChild(bg);

    const startKr = row.start === 'left' ? '왼쪽' : '오른쪽';
    const meta = `출발: ${startKr} · 사다리: ${row.lines}개`;
    const resKr = row.result === 'odd' ? '홀' : '짝';

    const badge = txt(historyBadgeLabel(row), 11, ML_LIST_TEXT, w - 52);
    badge.position.set(7, 5);

    const metaT = txt(meta, 11, ML_CARD_META, w - 52);
    metaT.position.set(7, 22);

    const blockT = txt(`🔗 Block #${row.block}`, 10, ML_BLOCK_LINK, w - 52);
    blockT.position.set(7, 40);
    blockT.interactive = true;
    blockT.cursor = 'pointer';
    blockT.hitArea = new PIXI.Rectangle(0, 0, Math.min(w - 16, blockT.width + 4), 18);
    blockT.on('pointertap', () => {
      window.open(
        `https://etherscan.io/block/${encodeURIComponent(String(row.block))}#consensusinfo`,
        '_blank',
        'noopener,noreferrer'
      );
    });

    c.addChild(badge);
    c.addChild(metaT);
    c.addChild(blockT);

    const cx = w - 23;
    const cy = h / 2;
    const circle = new PIXI.Graphics();
    const isOdd = row.result === 'odd';
    circle.lineStyle(1.5, isOdd ? 0x93c5fd : 0xfca5a5, 1);
    circle.beginFill(isOdd ? 0x2563eb : 0xdc2626, 1);
    circle.drawCircle(0, 0, 16);
    circle.endFill();
    circle.position.set(cx, cy);
    c.addChild(circle);

    const rt = txt(resKr, 12, 0xffffff, 40);
    rt.anchor.set(0.5);
    rt.position.set(cx, cy);
    c.addChild(rt);

    const badgeG = new PIXI.Graphics();
    badgeG.lineStyle(1.2, 0xfbbf24, 0.9);
    badgeG.beginFill(0x0f172a, 1);
    badgeG.drawCircle(0, 0, 6);
    badgeG.endFill();
    const bx = row.start === 'left' ? cx - 16 : cx + 16;
    /* 작은 노란 뱃지: 원 중심과 숫자 앵커를 동일하게 (이전엔 cy-15 로 글자만 위로 튐) */
    const rungBadgeY = cy - 11;
    badgeG.position.set(bx, rungBadgeY);
    c.addChild(badgeG);
    const rungT = txt(String(row.lines), 9, 0xfacc15, 20);
    rungT.anchor.set(0.5, 0.5);
    rungT.position.set(bx, rungBadgeY);
    c.addChild(rungT);

    return c;
  }

  /** ethminions setVerifyInputRandomness — 최근 회차 해시를 입력칸에 채움 */
  setVerifyRandomnessFromHash(hash: string) {
    if (!this.verifyDomInput || !hash) return;
    const val = String(hash).trim().toLowerCase();
    this.verifyDomInput.value = val.startsWith('0x') ? val : `0x${val}`;
  }

  destroyVerifyOverlay() {
    this.verifyDomHost?.remove();
    this.verifyDomHost = null;
    this.verifyDomInput = null;
    this.verifyDomResult = null;
  }

  private mountVerifyDom() {
    const root = this.canvasEl.parentElement;
    if (!root) return;
    const host = document.createElement('div');
    host.className = 'minions-verify-dock';
    host.setAttribute('aria-hidden', 'true');
    host.innerHTML = `
<div class="ml-verify-panel" role="region" aria-label="랜덤값 검증">
  <div class="ml-verify-title">검증해보기</div>
  <div class="ml-verify-formula">
    블록 정보중 Randomness 값 맨 끝자리만 보면 누구나 확인할 수 있어요.<br>
    1) 맨 마지막 글자: 0,2,4,6,8,a,c,e → 좌 / 1,3,5,7,9,b,d,f → 우<br>
    2) 끝에서 두 번째 글자: 0,2,4,6,8,a,c,e → 3줄 / 1,3,5,7,9,b,d,f → 4줄<br>
    3) 좌/우와 3/4줄 조합: 좌+3줄→짝, 좌+4줄→홀, 우+3줄→홀, 우+4줄→짝
  </div>
  <div class="ml-verify-input-row">
    <input id="minions-verify-randomness" class="ml-verify-input" type="text" autocomplete="off" placeholder="예: 0xabc..." />
    <button id="minions-verify-btn" class="ml-verify-btn" type="button">검증하기</button>
  </div>
  <div id="minions-verify-result" class="ml-verify-result">랜덤값(예: 0xabc...)을 넣고 검증하기를 눌러보세요.</div>
</div>`.trim();
    root.appendChild(host);
    this.verifyDomHost = host;
    this.verifyDomInput = host.querySelector('#minions-verify-randomness');
    this.verifyDomResult = host.querySelector('#minions-verify-result');
    const btn = host.querySelector('#minions-verify-btn');
    const run = () => {
      const v = (this.verifyDomInput?.value ?? '').trim();
      if (this.verifyDomResult) {
        this.verifyDomResult.textContent = formatVerifyResultEthminions(v);
      }
    };
    btn?.addEventListener('click', run);
    this.verifyDomInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') run();
    });
  }

  private syncVerifyDomOverlay(viewW: number, viewH: number) {
    if (!this.verifyDomHost) return;
    const show =
      !MINIONS_UI.disableHistory && this.historyDropOpen && this.rightPanel.visible;
    if (!show) {
      this.verifyDomHost.style.display = 'none';
      this.verifyDomHost.setAttribute('aria-hidden', 'true');
      return;
    }
    // layout 직후에는 getBounds(true)만으로 부모 월드 변환이 남아 도킹이 한 프레임 틀어질 수 있음.
    const b = this.verifyOverlayProbe.getBounds(false);
    if (!b.width || !b.height) {
      this.verifyDomHost.style.display = 'none';
      return;
    }
    const cr = this.canvasEl.getBoundingClientRect();
    const sx = cr.width / Math.max(1, viewW);
    const sy = cr.height / Math.max(1, viewH);
    const left = cr.left + b.x * sx;
    const top = cr.top + b.y * sy;
    const bw = b.width * sx;
    const bh = b.height * sy;
    const el = this.verifyDomHost;
    el.style.display = 'block';
    el.style.position = 'fixed';
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.width = `${Math.max(0, Math.round(bw))}px`;
    el.style.height = `${Math.max(0, Math.round(bh))}px`;
    el.style.zIndex = '30';
    el.style.pointerEvents = 'none';
    el.style.overflow = 'hidden';
    el.removeAttribute('aria-hidden');
    const panel = el.querySelector('.ml-verify-panel');
    if (panel instanceof HTMLElement) {
      panel.style.pointerEvents = 'auto';
      panel.style.maxHeight = '100%';
      panel.style.overflow = 'auto';
      panel.style.boxSizing = 'border-box';
    }
  }
}
